import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight, FileText, Printer } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser, user } = useAuth();
  const [status, setStatus] = useState("checking");
  const [guestClaim, setGuestClaim] = useState(null);
  const [pw, setPw] = useState("");
  const [pwDone, setPwDone] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const attempts = useRef(0);
  const sessionId = params.get("session_id");
  const formNumber = params.get("form");
  const bundle = params.get("bundle");
  const isGuest = params.get("guest") === "1";

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          try { const { data: r } = await api.get(`/payments/receipt/${sessionId}`); setReceipt(r); } catch { /* ignore */ }
          if (isGuest && !user) {
            try {
              const { data: claim } = await api.post("/payments/guest/claim-session", { session_id: sessionId });
              setGuestClaim(claim);
              if (claim.logged_in) await refreshUser();
            } catch { /* fall through to generic success */ }
          } else {
            await refreshUser();
          }
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* retry */ }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refreshUser, isGuest, user]);

  const savePassword = async () => {
    if (pw.length < 6) return;
    setPwBusy(true);
    try { await api.post("/auth/set-password", { password: pw }); setPwDone(true); }
    catch { /* ignore */ }
    finally { setPwBusy(false); }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-lg mx-auto px-6 py-32 text-center">
        {status === "checking" && (<><Loader2 className="mx-auto text-accent animate-spin mb-6" size={48} /><h1 className="font-serif text-4xl mb-3">Confirming your payment…</h1><p className="text-muted-foreground">This only takes a moment.</p></>)}
        {status === "paid" && (<><CheckCircle2 className="mx-auto text-accent mb-6" size={56} /><h1 className="font-serif text-5xl mb-3">You're all set!</h1>
          {receipt && (
            <div className="mt-2 mb-8 border-2 border-black/10 text-left" data-testid="receipt-card">
              <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-secondary/40">
                <span className="overline text-accent text-[0.6rem]">Receipt</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold" data-testid="receipt-total">${receipt.amount} {(receipt.currency || "usd").toUpperCase()}</span>
                  <button onClick={() => window.print()} data-testid="print-receipt-btn"
                    className="no-print inline-flex items-center gap-1.5 text-xs font-semibold text-accent hover:underline">
                    <Printer size={13} /> Print / Save
                  </button>
                </div>
              </div>
              <div className="px-5 py-4">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                  <span>NC Legal Forms</span>
                  <span>{new Date().toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                </div>
                <p className="text-sm font-medium mb-3">{receipt.label}</p>
                {receipt.items?.length > 0 ? (
                  <ul className="space-y-2" data-testid="receipt-items">
                    {receipt.items.map((it) => (
                      <li key={it.number} className="flex items-start gap-2 text-sm" data-testid={`receipt-item-${it.number}`}>
                        <FileText size={15} className="text-accent shrink-0 mt-0.5" />
                        <span><span className="font-mono text-xs text-muted-foreground">{it.number}</span> — {it.title}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-sm text-muted-foreground">Full Pro access is now active on your account.</p>}
              </div>
            </div>
          )}
          {formNumber ? (
            <><p className="text-muted-foreground mb-8 no-print">Your form <span className="font-mono font-medium">{formNumber}</span> is unlocked. Fill it, AI-complete it, and download it.</p>
            <Link to={`/forms/${formNumber}`} className="no-print inline-block"><Button data-testid="success-open-form-btn" className="rounded-sm h-12 px-7 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors gap-2">Open {formNumber} <ArrowRight size={18} /></Button></Link></>
          ) : bundle ? (
            <><p className="text-muted-foreground mb-8 no-print">Your {bundle}-form bundle is unlocked. Fill, AI-complete, save and download each one.</p>
            <Link to="/forms" className="no-print inline-block"><Button data-testid="success-bundle-btn" className="rounded-sm h-12 px-7 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors gap-2">Open my forms <ArrowRight size={18} /></Button></Link></>
          ) : (
            <><p className="text-muted-foreground mb-8 no-print">Your Pro membership is active. You can now fill, save and print any form.</p>
            <Link to="/forms" className="no-print inline-block"><Button data-testid="success-browse-btn" className="rounded-sm h-12 px-7 bg-primary hover:bg-accent hover:text-accent-foreground transition-colors gap-2">Start Filling Forms <ArrowRight size={18} /></Button></Link></>
          )}
          {guestClaim && guestClaim.logged_in && !pwDone && (
            <div className="mt-10 border-2 border-black/10 p-6 text-left no-print" data-testid="guest-setpw-card">
              <h3 className="font-serif text-xl mb-1">Add a password (optional)</h3>
              <p className="text-sm text-muted-foreground mb-4">You're signed in as a guest. Set a password so you can log back in anytime to your purchases.</p>
              <div className="flex gap-2">
                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="guest-setpw-input"
                  placeholder="Choose a password (min 6 chars)" className="rounded-sm" onKeyDown={(e) => e.key === "Enter" && savePassword()} />
                <Button onClick={savePassword} disabled={pwBusy || pw.length < 6} data-testid="guest-setpw-btn" className="rounded-sm shrink-0">Save</Button>
              </div>
            </div>
          )}
          {guestClaim && guestClaim.logged_in && pwDone && (
            <p className="mt-6 text-sm text-accent font-semibold" data-testid="guest-setpw-done">Password saved — you can log in with your email anytime.</p>
          )}
          {guestClaim && guestClaim.logged_in === false && (
            <div className="mt-10 border-2 border-black/10 p-6 text-left no-print" data-testid="guest-existing-card">
              <p className="text-sm text-muted-foreground">Your purchase was added to the existing account for <span className="font-medium text-foreground">{guestClaim.email}</span>. Please log in to access it.</p>
              <Link to="/login"><Button variant="outline" className="rounded-sm mt-4" data-testid="guest-existing-login-btn">Log in</Button></Link>
            </div>
          )}</>)}
        {(status === "error" || status === "timeout") && (<><XCircle className="mx-auto text-muted-foreground mb-6" size={56} /><h1 className="font-serif text-4xl mb-3">{status === "timeout" ? "Still processing" : "Payment issue"}</h1><p className="text-muted-foreground mb-8">{status === "timeout" ? "Your payment is taking longer than usual. Check your dashboard shortly." : "We couldn't confirm your payment. Please try again."}</p><Link to="/pricing"><Button variant="outline" className="rounded-sm">Back to Pricing</Button></Link></>)}
      </div>
    </div>
  );
}
